// ─── "Make it happen" — one map from a suggestion's CAPABILITY to what happens ─
// A "Worth a look" card carries `action`: an executable capability key, validated
// server-side against server/capabilities.js before it ever reaches the browser.
// Home used to IGNORE that key and always dispatch the campaign build, so a card
// titled "Set a BCW2027 pre-reg pace goal" handed the Owl "Build an email campaign
// for this suggestion and draft the audience it needs…" — and the Owl dutifully
// drafted a campaign plus a segment instead of the goal that was asked for
// (reported 2026-08-17). The capability IS the intent, so this module is the one
// place that turns it into either an Owl build prompt or the pre-filled editor
// deep link, and the button label says which.
//
// Two routes per capability, on purpose:
//   • Owl route  — the native chat has an act-tool for it (createGoal, createAlert,
//     createSegment, draftCampaign): it reads the suggestion, works out the metric
//     or audience from the data, and drafts for review. Used only when the client
//     has the native chat AND that act-tool's own flag is on.
//   • Editor route — the capability's deep link, editor pre-filled, human fills in
//     the rest. Always the fallback, and the only route for capabilities the Owl
//     has no tool for (an app community post).
// Nothing is ever created without a human approving it, either way.
//
// Keys MUST match server/capabilities.js CAPABILITIES — test/makeItHappen.test.js
// fails the build if the two lists drift apart.

const enc = (s) => encodeURIComponent(String(s || '').slice(0, 200));
// The suggestion's event, appended to every build prompt so the Owl scopes to it.
const forEvent = (suiteId) => (suiteId ? ` This is for event ${suiteId}.` : '');

export const MAKE_IT_HAPPEN = {
  email_campaign: {
    label: 'Make it happen',
    hint: 'Turn this suggestion into a campaign',
    perm: 'campaigns.approve',
    owlFlag: 'owl.draft_campaigns',
    // Prefer the Owl: it READS the suggestion and builds the right audience for it
    // via draftCampaign — so an "over-35 past buyers" idea doesn't get forced into
    // the abandoned-cart recipe.
    prompt: ({ goal, suiteId }) => `Build an email campaign for this suggestion and draft the audience it needs (create the segment if there isn't one), then show me the draft to review: "${goal}".${forEvent(suiteId)}`,
    // ?type is omitted on purpose so no recipe (abandoned cart) is auto-selected —
    // a blank audience beats a wrong one.
    href: ({ goal, suiteId }) => `/engage/campaigns?goal=${enc(goal)}${suiteId ? `&suite=${enc(suiteId)}` : ''}`,
  },
  create_goal: {
    label: 'Set the goal',
    hint: 'Draft the goal this suggests',
    perm: 'goals.manage',
    owlFlag: 'owl.create_goals',
    prompt: ({ goal, suiteId }) => `Draft a goal for this suggestion, then show me the draft to review: "${goal}". Work out which number it should track from the data (build the metric if no tile carries it), and pick a target and deadline that fit what the numbers are actually doing — pacing it against the same event last time if there is one.${forEvent(suiteId)}`,
    href: ({ title, suiteId }) => `/goals?${suiteId ? `new=${enc(suiteId)}&` : ''}name=${enc(title)}`,
  },
  create_alert: {
    label: 'Create the alert',
    hint: 'Draft the alert this suggests',
    perm: 'alerts.manage',
    owlFlag: 'owl.create_alerts',
    prompt: ({ goal, suiteId }) => `Set up an alert for this suggestion and show me the draft to review: "${goal}". Work out which number to watch and a threshold that would genuinely be worth being told about.${forEvent(suiteId)}`,
    href: ({ title, suiteId }) => `/alerts?${suiteId ? `new=${enc(suiteId)}&` : ''}name=${enc(title)}`,
  },
  create_segment: {
    label: 'Build the segment',
    hint: 'Build the audience this suggests',
    perm: 'campaigns.approve',
    owlFlag: 'owl.create_segments',
    prompt: ({ goal, suiteId }) => `Build the audience this suggestion needs as a saved segment, then show me the draft to review: "${goal}".${forEvent(suiteId)}`,
    href: ({ title }) => `/engage/segments?create=${enc(title)}`,
  },
  app_community_post: {
    label: 'Draft the post',
    hint: 'Draft a post to the app community',
    perm: 'content.manage',
    // No Owl act-tool for the app community feed — the composer is the route.
    owlFlag: null,
    prompt: null,
    href: ({ goal }) => `/engage/app/posts?compose=${enc(goal)}`,
  },
  follow_metric: {
    label: 'Follow this metric',
    hint: '',
    perm: 'dashboards.view',
    owlFlag: null,
    prompt: null,
    href: null,
    // Deliberately NO chip on a home card: following is the star on the tile itself,
    // and the card's own body click already opens that board. A second button to
    // the same place is noise, and we never show one we can't deliver.
    chip: false,
  },
};

// What "Make it happen" should do for a suggestion, or null when the card gets
// no chip. `flagIsOn(key)` answers for the CLIENT in context (pass
// (k) => flagOn(myFlags, k)); omit it and the Owl route is assumed available.
//   { kind: 'owl', prompt, suiteId, label, hint } → dispatch howler:owl-build
//   { kind: 'nav', to, label, hint }              → navigate to the pre-filled editor
export function resolveMakeItHappen(action, { goal = '', title = '', suiteId = '', owlNative = false, flagIsOn = null } = {}) {
  const cap = MAKE_IT_HAPPEN[action];
  if (!cap || cap.chip === false) return null;
  const label = cap.label; const hint = cap.hint;
  const owlOk = owlNative && cap.prompt && (!cap.owlFlag || !flagIsOn || flagIsOn(cap.owlFlag));
  if (owlOk) return { kind: 'owl', label, hint, suiteId, prompt: cap.prompt({ goal, title, suiteId }) };
  const to = cap.href ? cap.href({ goal, title: title || goal, suiteId }) : '';
  return to ? { kind: 'nav', label, hint, to } : null;
}
